import type { ErrorInfo, PropsWithChildren } from 'react';
import { Component } from 'react';

import { Button } from '@/components/ui/Button';
import { reportClientError } from '@/features/monitoring/lib/telemetry';
import { getErrorMessage } from '@/lib/error';

type ErrorBoundaryState = {
  hasError: boolean;
  message: string;
};

export class ErrorBoundary extends Component<PropsWithChildren, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = {
    hasError: false,
    message: ''
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, message: getErrorMessage(error) };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(error, errorInfo);
    void reportClientError({
      source: 'frontend-runtime',
      category: 'react-error-boundary',
      message: error.message,
      stackTrace: `${error.stack ?? ''}\n${errorInfo.componentStack}`
    });
  }

  private readonly resetBoundary = () => {
    this.setState({ hasError: false, message: '' });
  };

  public override render() {
    if (this.state.hasError) {
      return (
        <div className="grid min-h-screen place-items-center bg-slate-50 p-6 dark:bg-slate-950">
          <div className="max-w-md space-y-4 rounded-xl border border-slate-200/80 bg-white p-6 text-center shadow-soft dark:border-slate-700/90 dark:bg-slate-900/95">
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-sm text-slate-600 dark:text-slate-300">{this.state.message}</p>
            <Button onClick={this.resetBoundary}>Try again</Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
